import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getSafeAccessError } from "../api/platformAccessApi";
import { useAccessActions, useAccessData } from "../auth/AccessProvider";
import { routeAfterTenantSwitch } from "../auth/accessRouting";

export function TenantSwitcherList({
  tenants = [],
  activeTenantId,
  disabled = false,
  pending = false,
  error = "",
  onSelect,
}) {
  if (!Array.isArray(tenants) || tenants.length < 2) return null;

  return (
    <div className="pa-tenant-switcher">
      <p id="tenant-switcher-heading">Company</p>
      <ul aria-labelledby="tenant-switcher-heading">
        {tenants.map((tenant) => {
          const current = tenant.tenantId === activeTenantId;
          return (
            <li key={tenant.tenantId}>
              <button
                type="button"
                disabled={disabled || pending || current}
                aria-current={current ? "true" : undefined}
                onClick={() => onSelect(tenant.tenantId)}
              >
                <span>{tenant.displayName || tenant.tenantId}</span>
                {current ? <small>Current</small> : null}
              </button>
            </li>
          );
        })}
      </ul>
      {pending ? <p role="status">Switching company…</p> : null}
      {error ? <p role="alert">{error}</p> : null}
    </div>
  );
}

export default function TenantSwitcher({
  disabled = false,
  onBusyChange,
  onSwitched,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { availableTenants, activeTenant } = useAccessData();
  const { selectTenant } = useAccessActions();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const setBusy = (value) => {
    setPending(value);
    onBusyChange?.(value);
  };

  const handleSelect = async (tenantId) => {
    if (pending || tenantId === activeTenant?.tenantId) return;
    setBusy(true);
    setError("");
    try {
      const result = await selectTenant(tenantId);
      onSwitched?.();
      navigate(routeAfterTenantSwitch(result?.accessContext, location.pathname), {
        replace: true,
      });
    } catch (nextError) {
      setError(getSafeAccessError(
        nextError,
        "The company could not be switched. Please try again.",
      ));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TenantSwitcherList
      tenants={availableTenants}
      activeTenantId={activeTenant?.tenantId}
      disabled={disabled}
      pending={pending}
      error={error}
      onSelect={handleSelect}
    />
  );
}
